<template>
  <header class="hdf-header03">
    <div class="nav-bar">
      <div class="nav-inner">
        <nav class="nav-left">
          <template v-for="(item, index) in navInfoList" :key="index">
            <!-- 无子级：直接跳转 -->
            <a
              v-if="!item.children || item.children.length == 0"
              href="javascript:;"
              class="nav-item"
              @click="jumpTo(item)"
            >{{ item.name }}</a>

            <!-- 有子级：下拉框 -->
            <el-dropdown
              v-else
              trigger="click"
              placement="bottom-start"
              :hide-on-click="true"
            >
              <span class="nav-item nav-dropdown">
                {{ item.name }}
                <el-icon class="nav-arrow"><arrow-down /></el-icon>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item
                    v-for="(subitem, si) in item.children"
                    :key="si"
                    @click="jumpTo(subitem)"
                  >{{ subitem.name }}</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
        </nav>

        <div class="nav-right">
          <!-- 未登录 -->
          <template v-if="!isLogin">
            <div class="user-btn" @click="goPath('/signin')">
              <span class="user-text">
                <span class="zh">登录 / 注册</span>
                <span class="en">Sign in</span>
              </span>
            </div>
          </template>
          <!-- 已登录 -->
          <template v-else>
            <div v-if="roleLabel" class="role-tag" :class="roleTagClass">{{ roleLabel }}</div>
            <div class="user-btn" @click="goPath('/personalRole')">
              <img class="user-avatar" src="@/assets/images/v2/icon_user.png" alt="" />
              <span class="user-text">
                <span class="zh">个人中心</span>
                <span class="en">Personal Center</span>
              </span>
            </div>
            <div class="user-btn" @click="goLogout">
              <span class="user-text">
                <span class="zh">退出登录</span>
                <span class="en">Log out</span>
              </span>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- 滚动通知条（有通知才显示）：红色文字横向滚动，无关闭按钮 -->
    <div class="notice-bar" v-if="configState.noticeList.value.length > 0">
      <div class="notice-track">
        <span
          v-for="(notice, ni) in noticeMarqueeList"
          :key="ni"
          class="notice-item"
          @click="handleNoticeClick(notice)"
        >{{ notice.context }}</span>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowDown } from '@element-plus/icons-vue'
import { Logout } from '@/api/api.js'
import { getUserInfo } from '@/utils/common'
import { jumpBlankUrl } from '@/utils/jump'
import { useConfigStore } from '@/stores'
import { storeToRefs } from 'pinia'

const router = useRouter()

// ===================== 老导航逻辑（与 HDFHeader2 一致） =====================
const configStore = useConfigStore()
const configState = storeToRefs(configStore)

// 导航数据：App.vue 启动时通过 apiGetNavInfoList 拉取并存入 store
const navInfoList = configState.navInfoList

// 通知条：复制两份实现无缝横向滚动
const noticeMarqueeList = computed(() => {
  const list = configState.noticeList.value || []
  return [...list, ...list]
})

// 登录状态（沿用原有 token 判断方式）
const isLogin = ref(!!localStorage.getItem('token'))
configStore.setIsLogin(!localStorage.getItem('token'))

// 用户身份标注（老师/学生），登录时后端已返回 role
const userInfo = getUserInfo()
const ROLE_LABELS = { teacher: '老师', student: '学生' }
const roleLabel = ROLE_LABELS[userInfo && userInfo.role] || ''
const roleTagClass = userInfo && userInfo.role === 'teacher' ? 'role-teacher' : 'role-student'

// 统一跳转请求（与 NewHeader 一致的新版逻辑）
// 新会员体系：后端按登录态解析导航——
//   未登录：分组链接只下发名称和 needLogin 标记，点击提示登录；
//   已登录但无权限：分组链接 url 置空，提示未开通
const jumpTo = (item) => {
  if (!item) { return }
  if (item.needLogin) {
    ElMessage('请登录后再试')
    router.push('/signIn')
    return
  }
  const url = item.url
  if (!url || url == '') {
    if (item.navGroup) {
      ElMessage('暂未开通该功能，请联系管理员')
    }
    return
  }
  if (url === 'deving') {
    handleDeveloping()
    return
  }
  if (url.startsWith('/')) {
    window.location.href = url
  } else {
    jumpBlankUrl(url, userInfo)
  }
}

// 通知条
const handleNoticeClick = (notice) => {
  if (notice && notice.url) {
    window.open(notice.url, '_blank')
  }
}

const goPath = (path) => {
  router.push(path)
}

const goLogout = () => {
  Logout({
    Authorization: localStorage.getItem('token')
  }).then((res) => {
    localStorage.clear()
    isLogin.value = false
    configStore.setIsLogin(true)
    router.push('/signin')
  })
}

// 待开发功能提示
const handleDeveloping = () => {
  ElMessage('开发中...')
}
</script>

<style scoped>
/* 新版导航：蓝色通栏，内容 1440px 居中（外层缩放容器负责等比适配） */
.hdf-header03 {
  width: 100%;
}
.nav-bar {
  width: 100%;
  background: #3A69B4;
}

/* 通知条：白底红字，横向无缝滚动，无关闭按钮 */
.notice-bar {
  width: 100%;
  background: #ffffff;
  border-bottom: 1px solid #e4e7ed;
  overflow: hidden;
  padding: 10px 0;
}
.notice-track {
  display: inline-flex;
  white-space: nowrap;
  will-change: transform;
  animation: notice-marquee 30s linear infinite;
}
.notice-bar:hover .notice-track {
  animation-play-state: paused;
}
.notice-item {
  color: #e60012;
  font-size: 15px;
  font-weight: 500;
  margin-right: 120px;
  cursor: pointer;
}
@keyframes notice-marquee {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
.nav-inner {
  width: 1440px;
  margin: 0 auto;
  height: 84px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px;
  box-sizing: border-box;
}
.nav-left {
  display: flex;
  align-items: center;
  gap: 80px;
}
.nav-item {
  font-size: 20px;
  color: #ffffff;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  transition: opacity 0.2s;
}
.nav-item:hover {
  opacity: 0.85;
  text-decoration: none;
}
.nav-dropdown {
  display: inline-flex;
  align-items: center;
  outline: none;
}
.nav-arrow {
  margin-left: 4px;
  font-size: 14px;
  color: #ffffff;
}
.nav-right {
  display: flex;
  align-items: center;
  gap: 48px;
}
.user-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: opacity 0.2s;
}
.user-btn:hover {
  opacity: 0.85;
}
.user-avatar {
  width: 27px;
  height: 27px;
  display: block;
}
.user-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}
.user-text .zh {
  font-size: 15px;
  color: #ffffff;
  font-weight: 600;
}
.user-text .en {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 2px;
}
.role-tag {
  display: flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
}
.role-teacher {
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fcd34d;
}
.role-student {
  color: #15803d;
  background: #dcfce7;
  border: 1px solid #86efac;
}
</style>
